import { useQuery } from '@tanstack/react-query';
import { LIST_BADGE_LIFECYCLE_STATE } from '@terra/shared-dto-types';
import { terraProxyApiClient } from '../../clients/testerClient';
import { TARGET_TYPE } from '../../utils/target-scope.utils';
import { countFindingsForTarget } from '../api/target-findings-api';
import { neoExecutionsCountsQueryOptions } from './neo-executions-count';
import { resolveAiApplicationComponentKpis } from './resolve-ai-application-component-kpis';

const STALE_TIME_MS = 30_000;

export type AiApplicationKpiMetrics = {
  findings: number;
  signals: number;
  agents: number;
  components: number;
  executions: number;
};

export function useAiApplicationKpiMetrics({
  aiApplicationId,
}: {
  aiApplicationId: string | null;
}) {
  const coreQuery = useQuery({
    queryKey: ['ai-application-kpi-core', aiApplicationId],
    queryFn: async () => {
      const id = aiApplicationId ?? '';
      const [findings, signalsCount, componentsResponse, application] =
        await Promise.all([
          countFindingsForTarget({
            targetId: id,
            targetType: TARGET_TYPE.AI_APPLICATION,
            filter: { lifecycleState: LIST_BADGE_LIFECYCLE_STATE },
          }),
          terraProxyApiClient.getSignalsCountByApplicationId(id),
          terraProxyApiClient.getAiApplicationComponents(id),
          terraProxyApiClient.getAiApplication(id),
        ]);

      const { agents, components } = resolveAiApplicationComponentKpis({
        components: componentsResponse.components,
        aiKind: application.kind,
      });

      return {
        findings,
        signals: signalsCount?.total ?? 0,
        agents,
        components,
      };
    },
    enabled: Boolean(aiApplicationId),
    staleTime: STALE_TIME_MS,
  });

  const executionsQuery = useQuery({
    ...neoExecutionsCountsQueryOptions(aiApplicationId ?? ''),
    enabled: Boolean(aiApplicationId),
  });

  const core = coreQuery.data;
  const stats: AiApplicationKpiMetrics | undefined = core
    ? {
        ...core,
        executions: executionsQuery.data?.total ?? 0,
      }
    : undefined;

  return {
    stats,
    isLoadingCoreMetrics: coreQuery.isLoading,
    isLoadingExecutions: executionsQuery.isLoading,
  };
}
